import { faEnvelope, faPencil, faPhone, faPlus, faTrash, faUserTie } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { useToast } from '@shared/hooks/useToast';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BeepingTeamContacts } from '../../../List/models/Clients';
import { useDeleteTeamContact } from '../../hooks/useDeleteTeamContact';
import { AddContactModal } from './AddContactModal';
import { EditContactModal } from './EditContactModal';

interface ContactsTabProps {
  contacts: BeepingTeamContacts[];
  teamId: number;
  onRefresh?: () => void;
}

export const ContactsTab = ({ contacts, teamId, onRefresh }: ContactsTabProps) => {
  const { t } = useTranslation('viewClientModule');
  const { toast } = useToast();
  const { deleteContactAsync, isPending: isDeleting } = useDeleteTeamContact(teamId);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<BeepingTeamContacts | null>(null);

  const handleEdit = (contact: BeepingTeamContacts) => {
    setSelectedContact(contact);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (contact: BeepingTeamContacts) => {
    if (!contact.id) return;

    if (!confirm(t('tabs.contacts.deleteConfirm', { name: contact.name }))) {
      return;
    }

    try {
      await deleteContactAsync(contact.id);
      toast({
        title: t('tabs.contacts.deleteSuccess.title'),
        description: t('tabs.contacts.deleteSuccess.description'),
      });
      onRefresh?.();
    } catch (error) {
      toast({
        title: t('tabs.contacts.deleteError.title'),
        description: t('tabs.contacts.deleteError.description'),
        variant: 'destructive',
      });
    }
  };

  const AddContactButton = (
    <Button variant="outline" size="sm" onClick={() => setIsAddModalOpen(true)}>
      <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
      {t('tabs.contacts.addContact')}
    </Button>
  );

  if (contacts.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-lg mb-1">{t('tabs.contacts.sectionTitle')}</h2>
            <p className="text-sm text-muted-foreground">{t('tabs.contacts.sectionDescription')}</p>
          </div>
          {AddContactButton}
        </div>
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground border border-dashed border-card-border rounded-lg">
          <FontAwesomeIcon icon={faUserTie} className="w-12 h-12 mb-4 opacity-50" />
          <p className="mb-4">{t('tabs.contacts.empty')}</p>
          <Button variant="outline" size="sm" onClick={() => setIsAddModalOpen(true)}>
            <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
            {t('tabs.contacts.addFirstContact')}
          </Button>
        </div>
        <AddContactModal open={isAddModalOpen} onOpenChange={setIsAddModalOpen} teamId={teamId} onSuccess={onRefresh} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-lg mb-1">{t('tabs.contacts.sectionTitle')}</h2>
          <p className="text-sm text-muted-foreground">{t('tabs.contacts.sectionDescription')}</p>
        </div>
        {AddContactButton}
      </div>

      <div className="border border-card-border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('tabs.contacts.name')}</TableHead>
              <TableHead>{t('tabs.contacts.email')}</TableHead>
              <TableHead>{t('tabs.contacts.phone')}</TableHead>
              <TableHead>{t('tabs.contacts.role')}</TableHead>
              <TableHead className="text-right">{t('tabs.contacts.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.map((contact, index) => (
              <TableRow key={contact.id ?? index}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <FontAwesomeIcon icon={faUserTie} className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-medium">{contact.name}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {contact.email ? (
                    <a
                      href={`mailto:${contact.email}`}
                      className="text-primary hover:underline flex items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faEnvelope} className="w-3 h-3" />
                      {contact.email}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {contact.phone ? (
                    <a href={`tel:${contact.phone}`} className="text-primary hover:underline flex items-center gap-2">
                      <FontAwesomeIcon icon={faPhone} className="w-3 h-3" />
                      {contact.phone}
                    </a>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  {contact.role ? (
                    <Badge variant="outline">{contact.role}</Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {contact.id && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(contact)} disabled={isDeleting}>
                          <FontAwesomeIcon icon={faPencil} className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(contact)}
                          disabled={isDeleting}
                          className="text-destructive hover:text-destructive"
                        >
                          <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="text-sm text-muted-foreground">{t('tabs.contacts.total', { count: contacts.length })}</p>

      <AddContactModal open={isAddModalOpen} onOpenChange={setIsAddModalOpen} teamId={teamId} onSuccess={onRefresh} />
      <EditContactModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        teamId={teamId}
        contact={selectedContact}
        onSuccess={onRefresh}
      />
    </div>
  );
};
